import { withTranslation } from 'react-i18next';

import DataTable from '../../../../components/Datatable';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { MAX_ROW_LIMIT, ROW_CAP, ROW_CHUNK_SIZE } from '../../GscTools/query';
import { RETRIEVAL_COLUMNS } from '../keys';

/**
 * Block 05 — how much was retrieved, and the export of exactly that.
 *
 * The point of the block is to stop the table above it being read as an inventory. Each period
 * has its own cursor, each one stopped for its own reason, and neither reaching the end of what
 * Search Console will return proves the property has no further rows — so the completeness row
 * says "not guaranteed" rather than quietly implying it is.
 *
 * The export is of every row retrieved, not the page on screen, and it is disabled while there is
 * no successful generation to export: a file that silently held the previous scope's rows would
 * outlive the only context that explained it.
 */
function Retrieval({ t, scope, status, retrieval, rowCount, exportable, onExport, fetchedAt }) {
    const columns = RETRIEVAL_COLUMNS(t);

    const count = (value) =>
        value === null || value === undefined
            ? t('tools.gsc.not_returned')
            : t('tools.pages_queries_changes.retrieval.rows_value', { count: value });

    const cursor = (next) =>
        next === null || next === undefined
            ? t('tools.pages_queries_changes.retrieval.cursor_done')
            : t('tools.pages_queries_changes.retrieval.cursor_at', { row: next });

    const rows = [
        {
            id: 'retrieved',
            measure: t('tools.pages_queries_changes.retrieval.retrieved_rows'),
            current: count(retrieval?.currentRows),
            baseline: scope.baseline
                ? count(retrieval?.priorRows)
                : t('tools.gsc.coverage.no_baseline'),
        },
        {
            id: 'completeness',
            measure: t('tools.pages_queries_changes.retrieval.completeness'),
            current: t('tools.pages_queries_changes.retrieval.not_guaranteed'),
            baseline: t('tools.pages_queries_changes.retrieval.not_guaranteed'),
        },
        {
            id: 'cursor',
            measure: t('tools.pages_queries_changes.retrieval.cursor'),
            current: cursor(retrieval?.currentNextOffset),
            baseline: scope.baseline
                ? cursor(retrieval?.priorNextOffset)
                : t('tools.gsc.coverage.no_baseline'),
        },
        {
            id: 'joined',
            measure: t('tools.pages_queries_changes.retrieval.joined_rows'),
            current: count(rowCount),
            baseline: t('tools.pages_queries_changes.retrieval.outer_join'),
        },
    ];

    return (
        <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
            <div className="card-header d-flex justify-content-between">
                <div>
                    <p className="card-title">{t('tools.pages_queries_changes.retrieval.title')}</p>
                    <p className="gsc-tool-sub">
                        {t('tools.pages_queries_changes.retrieval.description')}
                    </p>
                </div>

                <div className="gsc-tool-icon-actions">
                    <button
                        type="button"
                        title={t('tools.pages_queries_changes.retrieval.export')}
                        aria-label={t('tools.pages_queries_changes.retrieval.export')}
                        onClick={onExport}
                        disabled={!exportable || status !== 'done'}
                    >
                        <i className="icon-fi_download" />
                    </button>
                </div>
            </div>

            <div className="card-body">
                <DataTable
                    nodes={rows}
                    COLUMNS={columns}
                    sortFns={getSortKeys(columns)}
                    additionalStyles={setAdditionalStyles(columns)}
                    onlyTable
                    notUseDefaultStyle
                    customContainerStyles="gsc-tool-rules-table"
                />

                <p className="gsc-tool-table-note">
                    {t('tools.pages_queries_changes.retrieval.paging', {
                        chunk: ROW_CHUNK_SIZE,
                        cap: ROW_CAP,
                        limit: MAX_ROW_LIMIT,
                    })}
                </p>
                <p className="gsc-tool-table-note">
                    {t('tools.pages_queries_changes.retrieval.export_note')}
                    {!!fetchedAt && <> {t('tools.gsc.coverage.fetched', { time: fetchedAt })}</>}
                </p>
            </div>
        </div>
    );
}

export default withTranslation()(Retrieval);
